import { useTranslation } from 'react-i18next';
import { Clock, Calendar, Repeat, CalendarRange, CalendarDays, Briefcase, Edit2, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { AvailabilityMode, WizardStep } from '../hooks/useAvailabilityWizard';
import type { ServiceOption } from '../../CreateAvailabilityDialog';

interface ReviewStepProps {
  mode: AvailabilityMode;
  summary: string;
  startTime: string;
  endTime: string;
  services: ServiceOption[];
  selectedServiceIds: number[];
  onEditStep: (step: WizardStep) => void;
}

const MODE_CONFIG: Record<AvailabilityMode, { icon: typeof Repeat; labelKey: string }> = {
  recurring: { icon: Repeat, labelKey: 'availability.dialog.recurring.label' },
  date_range: { icon: CalendarRange, labelKey: 'availability.dialog.dateRange.label' },
  one_time: { icon: CalendarDays, labelKey: 'availability.dialog.oneTime.label' },
};

export function ReviewStep({
  mode,
  summary,
  startTime,
  endTime,
  services,
  selectedServiceIds,
  onEditStep,
}: ReviewStepProps) {
  const { t } = useTranslation('staff');

  const ModeIcon = MODE_CONFIG[mode].icon;
  const modeLabel = t(MODE_CONFIG[mode].labelKey);

  const selectedServices = services.filter((s) => selectedServiceIds.includes(s.id));

  return (
    <div className="p-6 space-y-4">
      {/* Success header */}
      <div className="text-center py-4">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">
          {t('availability.wizard.review.ready', 'Ready to Create')}
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          {t('availability.wizard.review.reviewDetails', 'Review your availability details')}
        </p>
      </div>

      {/* Review cards */}
      <div className="space-y-3">
        {/* Type card */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                <ModeIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                  {t('availability.wizard.review.type', 'Type')}
                </p>
                <p className="text-base font-medium text-gray-900 mt-0.5">{modeLabel}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEditStep(1)}
              className="h-8 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              <Edit2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Schedule card */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                <Calendar className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                  {t('availability.wizard.review.schedule', 'Schedule')}
                </p>
                <p className="text-base font-medium text-gray-900 mt-0.5">{summary}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEditStep(3)}
              className="h-8 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              <Edit2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Time card */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                  {t('availability.wizard.review.timeRange', 'Time Range')}
                </p>
                <p className="text-base font-medium text-gray-900 mt-0.5">
                  {startTime} – {endTime}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEditStep(3)}
              className="h-8 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              <Edit2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Services card (if any selected) */}
        {selectedServices.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                  <Briefcase className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                    {t('availability.wizard.review.services', 'Services')} ({selectedServices.length})
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {selectedServices.map((service) => (
                      <span
                        key={service.id}
                        className="inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-sm font-medium border border-emerald-200"
                      >
                        {service.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEditStep(2)}
                className="h-8 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              >
                <Edit2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
